import {useStartSinglePlayerTrigger} from '@/hooks/useStartSinglePlayerTrigger'
import {EScene} from '@/types/scene.types'
import {isEventGameCode} from '@repo/shared/Customer/Coop/utils/gameCodeTypes'
import {getGameCodeUrlParam, removeGameCodeUrlParam} from '@repo/shared/utils/gameCode.utils'
import {isSinglePlayerMode} from '@repo/shared/utils/gameMode'
import {useSetAtom} from 'jotai'
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {gameCodeState, sceneState} from '../Store/atoms'

type ContextType = {
  resetInit: () => void
}

export const InitContext = createContext<ContextType>(null!)

export const useInitContext = () => useContext(InitContext)

export const InitProvider: FC<PropsWithChildren> = ({children}) => {
  const setGameCode = useSetAtom(gameCodeState)
  const setScene = useSetAtom(sceneState)

  const {startSinglePlayer} = useStartSinglePlayerTrigger()

  const didInit = useRef(false)
  const [isReady, setIsReady] = useState(false)

  const resetInit = useCallback(() => {
    didInit.current = false
    setIsReady(false)
  }, [])

  // The initial check
  useEffect(() => {
    if (didInit.current) return
    didInit.current = true

    const gameCodeParam = getGameCodeUrlParam()

    if (gameCodeParam && isEventGameCode(gameCodeParam)) {
      // start as event game
      setGameCode(gameCodeParam)
      setScene(EScene.LOBBY)

      removeGameCodeUrlParam()

      setIsReady(true)
    } else if (isSinglePlayerMode()) {
      // start single player
      startSinglePlayer()
      setIsReady(true)
    } else {
      // start as local game
      setScene(EScene.PRE_LOBBY)

      removeGameCodeUrlParam()

      setIsReady(true)
    }
  }, [didInit.current])

  if (!isReady) return null

  return <InitContext.Provider value={{resetInit}}>{children}</InitContext.Provider>
}
